import { useSelector } from 'react-redux';

import { PaymentSystem, useGooglePay } from '@web-solutions/react-billing';

import { InertialApplePay, ModeApplePay, ModePayPal, ModeGooglePay, ModeMercadoPago, } from 'core/constants/billing';
import { useApplePayCanMakePayments } from 'core/hooks/use-apple-pay-can-make-payments';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { ProductDetails, selectPaymentSystem } from 'core/store/billing/selectors';

export const useShowPayPal = (product?: ProductDetails) => {
  const { modePayPal } = useRemoteConfig();

  const applePayCanMakePaymentsWithActiveCard = useApplePayCanMakePayments(true);

  return (
    (
      !product?.isOneTimePurchase
      &&
      (
        modePayPal === ModePayPal.ENABLED ||
        modePayPal === ModePayPal.SOLIDGATE ||
        (modePayPal === ModePayPal.DISABLED_IF_APPLE_PAY_WITH_CARD && applePayCanMakePaymentsWithActiveCard === false)
      )
    )
    ||
    (
      !!product?.isOneTimePurchase
      &&
      modePayPal === ModePayPal.SOLIDGATE
    )
  );
};

export const useShowApplePay = () => {
  const { modeApplePay } = useRemoteConfig();

  const available = useApplePayCanMakePayments(modeApplePay === ModeApplePay.ENABLED_IF_WITH_CARD);

  return modeApplePay !== ModeApplePay.DISABLED && available;
}

/**
 * Stripe has no Google Pay SDK of its own to ask: the wallet rides on the
 * browser's Payment Request API, which is exactly what Chrome/Edge/Android
 * expose and Firefox does not. Safari exposes it too, but there it means Apple
 * Pay — `ApplePaySession` is the tell, and that sheet belongs to Apple Pay.
 *
 * The final say is Stripe's own `canMakePayment().googlePay` inside
 * `GooglePayButton`; this check only keeps the modal from reserving a slot (and
 * collapsing the card form behind a toggle) on browsers that have no chance.
 */
const isStripeGooglePayCapable = () =>
  typeof window !== 'undefined'
  // Stripe refuses to set up Payment Request over plain HTTP and says so in the
  // console — don't even ask it to on a local http:// dev server.
  && window.location?.protocol === 'https:'
  && !!(window as any).PaymentRequest
  && !(window as any).ApplePaySession;

export const useShowGooglePay = () => {
  const { modeGooglePay } = useRemoteConfig();
  const paymentSystem = useSelector(selectPaymentSystem);

  const available = useGooglePay(modeGooglePay === ModeGooglePay.ENABLED_IF_PAYMENT_METHOD_PRESENT);

  // Google's `isReadyToPay` (behind `useGooglePay`) needs pay.js, which only the
  // Solidgate SDK brings in — on Stripe it never resolves, so the flag alone
  // plus the capability check above decide.
  if (paymentSystem === PaymentSystem.STRIPE) {
    return modeGooglePay !== ModeGooglePay.DISABLED && isStripeGooglePayCapable();
  }

  return modeGooglePay !== ModeGooglePay.DISABLED && available;
}

export const useShowInertialApplePay = () => {
  const { inertialApplePay } = useRemoteConfig();
  const paymentSystem = useSelector(selectPaymentSystem);

  const applePayCanMakePaymentsWithActiveCard = useApplePayCanMakePayments(true);

  // Never for TailoredPay. The shortcut works by finding an Apple Pay button in
  // the document and clicking it instead of opening the modal — but TailoredPay's
  // button is rendered by Collect.js *inside* the payment modal, which is mounted
  // (hidden) at all times. So the shortcut would find that hidden button, click it
  // to no effect, and return without ever opening the form.
  //
  // Collect.js on `window` is checked as well as the payment system in the store:
  // it is injected only for TailoredPay, and unlike the store it cannot be empty
  // because of hydration order or a fallback that has not resolved yet.
  const isTailoredpay =
    paymentSystem === PaymentSystem.TAILOREDPAY
    || (typeof window !== 'undefined' && Boolean((window as any).CollectJS));

  if (isTailoredpay) {
    return false;
  }

  return (inertialApplePay === InertialApplePay.ENABLED || inertialApplePay === InertialApplePay.ENABLED_WITH_DEFAULT) || (inertialApplePay === InertialApplePay.ENABLED_IF_WITH_CARD && applePayCanMakePaymentsWithActiveCard);
}

export const useShowMercadoPago = () => {
  const { modeMercadoPago } = useRemoteConfig();

  return modeMercadoPago === ModeMercadoPago.ENABLED;
};
